import { api, ApiError } from './api.js';

export interface User { id: string; name: string; email: string; userType: 'jobseeker' | 'hr' | 'admin' }
interface ApiUser { id: string; name: string; email: string; role: User['userType'] }
const mapUser = (u: ApiUser): User => ({ id: u.id, name: u.name, email: u.email, userType: u.role });

/** One entry of the caller's active-membership list — enough to name a tenant in the picker, to
 *  send it back as ?companyId=, and to say what state that tenant is in. Every entry is a company
 *  the caller may act as RIGHT NOW: /api/me builds this from listActiveMemberships, whose join
 *  requires both the seat and the company to be active, so a suspended employer is deliberately
 *  absent from it.
 *
 *  `status` and `isComplete` are per-entry, not only on `company` below, and that is what lets
 *  the client follow the PICKER. `company` is membership row 0 whatever ?companyId= the rest of
 *  the app is sending, so reading completeness off it told a recruiter who had switched tenants
 *  about the wrong employer. resolveActiveCompany (src/lib/companySession.ts) reads the entry
 *  matching the active id instead. `status` is 'active' on every entry by construction; it is
 *  carried so companyState() can read an entry directly rather than assume it. */
export interface SessionCompany {
  id: string; slug: string; name: string; companyRole: 'owner' | 'member';
  status: string; isComplete: boolean;
}

/** What /api/me reports as the session's company: membership row 0, or — when there is no active
 *  membership at all — the SUSPENDED one, which is the only way a screen can name the employer
 *  that was taken down. It is NOT the picked tenant; nothing here follows ?companyId=, which is
 *  why resolveActiveCompany reads `companies` and falls back to this only when that list is empty.
 *
 *  Richer than a list entry by the profile text the /hr/profile form round-trips. `status` and
 *  `isComplete` are inherited from SessionCompany — the gate needs both to say WHY the HR app is
 *  unusable rather than only that it is, and `isComplete` is the same predicate that gates
 *  publishing and task generation on the server (isCompanyComplete, routes/company.ts).
 *
 *  Null for a seeker, an admin, and for an HR account admin provisioning has not attached to a
 *  company yet. NOT null for a suspended one — that case reports here with status 'suspended'
 *  and an empty `companies`. */
export interface SessionActiveCompany extends SessionCompany {
  website: string;
  description: string;
  logoKey: string | null;
}

export interface Session { user: User; company: SessionActiveCompany | null; companies: SessionCompany[] }

export async function me(): Promise<Session | null> {
  try {
    const res = await api<{ user: ApiUser; company?: SessionActiveCompany | null; companies?: SessionCompany[] }>('/api/me');
    return { user: mapUser(res.user), company: res.company ?? null, companies: res.companies ?? [] };
  } catch (e) { if (e instanceof ApiError && e.status === 401) return null; throw e; }
}
export async function login(email: string, password: string): Promise<User> {
  return mapUser((await api<{ ok: true; user: ApiUser }>('/api/auth/login', { method: 'POST', body: { email, password } })).user);
}
export const logout = () => api<{ ok: true }>('/api/auth/logout', { method: 'POST' });
export const signup = (input: { email: string; password: string; name: string; role: 'jobseeker' | 'hr' }) =>
  api<{ ok: true; message: string }>('/api/auth/signup', { method: 'POST', body: input });
export const resendVerification = (email: string) =>
  api<{ ok: true; message: string }>('/api/auth/resend-verification', { method: 'POST', body: { email } });
export const forgot = (email: string) => api<{ ok: true; message: string }>('/api/auth/forgot', { method: 'POST', body: { email } });
export const resetPassword = (token: string, password: string) =>
  api<{ ok: true }>('/api/auth/reset', { method: 'POST', body: { token, password } });
export const verifyEmail = (token: string) => api<{ ok: true }>('/api/auth/verify', { method: 'POST', body: { token } });
